'use client';

import { useCatalogContext, useCiCSearch } from '@dealerportal/catalog-ui';
import { GROUP_REF_SRP_PRIORITY } from '@dealerportal/catalog-ui';
import type { PaginatedProduct } from '@dealerportal/common';
import { logger } from '@dealerportal/core';
import { isValidQueryString } from '@dealerportal/utilities';
import { useEffect, useState } from 'react';

export type ProductSearchResults = {
  total: number;
  products: PaginatedProduct[];
  loading: boolean;
};

// Convert query string to CiC characteristics format
const convertQueryToCharacteristics = (
  query: string
): Record<string, string[]> => {
  const characteristics: Record<string, string[]> = {};

  if (!query) return characteristics;

  // Parse query string parameters
  const queryParams = new URLSearchParams(query);

  queryParams.forEach((value, key) => {
    switch (key) {
      case 'type':
        // strip special characters including hyphens and lowercase (e.g. "luxury-vinyl" -> "luxuryvinyl")
        characteristics['type'] = value
          .split(',')
          .map((type) =>
            type.indexOf('.') === -1
              ? type.toLowerCase().replace(/[^a-zA-Z0-9]/g, '')
              : type
          );
        break;
      case 'brand':
        characteristics['brand'] = value.split(',');
        break;
      case 'line':
        characteristics['line'] = value.split(',');
        break;
      case 'color-tones':
        // Maps to "colortone" in catalog.json
        characteristics['colortone'] = value.split(',');
        break;
      case 'color-range':
        // Maps to "colorfamily" in catalog.json
        characteristics['colorfamily'] = value.split(',');
        break;
      case 'tags':
      case 'feature':
        // strip special characters and lowercase (e.g. "in-stock" -> "webinstock")
        characteristics['merchandisings'] = value
          .split(',')
          .map((tag) => `web${tag.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()}`);
        break;
      case 'excludeUpid':
      case 'spreadProducts':
        break;
      default:
        // Handle other characteristics
        if (value && key !== 'numResults') {
          characteristics[key] = value.split(',');
        }
        break;
    }
  });

  return characteristics;
};

export function useProductSearch(
  query: string | string[] | (() => string | string[]),
  numResults = 5,
  excludeVariants = false
) {
  const { useCiC, catalogVersionId, loaded, searchWithoutGroupRef } =
    useCatalogContext();
  const [results, setResults] = useState<ProductSearchResults>({
    products: [],
    total: 0,
    loading: true, // Added for loading skeleton UI
  });

  // Initialize CiC search hook
  const { search } = useCiCSearch();

  query = typeof query === 'function' ? query() : query;
  query = Array.isArray(query) ? query.join('&') : query;
  query = query.startsWith('?') ? query : `?${query}`;

  useEffect(() => {
    if (!isValidQueryString(query as string)) {
      // if invalid, stop loading.
      setResults((prev) => ({ ...prev, loading: false }));
      return;
    }
    // if valid, start loading
    setResults((prev) => ({ ...prev, loading: true }));

    const abortController = new AbortController();
    const signal = abortController.signal;

    // Use CiC search if useCiC is true and catalogVersionId is provided
    if (useCiC) {
      const performCicSearch = async () => {
        logger.info('Performing CiC search with query:', query);
        try {
          // Convert query to characteristics
          const characteristics = convertQueryToCharacteristics(
            query as string
          );

          // Call CiC search (use page 1 for simple search)
          const result = await search({
            catalogVersionId: catalogVersionId ?? '-1',
            initialPage: 1,
            characteristics,
            excludeFilters: true,
            pageSize: numResults,
            excludeVariants,
            groupRefs:
              !characteristics['spreadProducts'] && !searchWithoutGroupRef
                ? [GROUP_REF_SRP_PRIORITY]
                : undefined, // Group by srp_priority if spreadProducts and searchWithoutGroupRef are false
          });

          if (!signal.aborted) {
            // Transform result to match ProductSearchResults interface
            setResults({
              products: result.products as PaginatedProduct[],
              total: result.colorCount || 0,
              loading: false,
            });
          }
        } catch (error) {
          if (!signal.aborted) {
            logger.withError(error).error('CiC search error:');
            setResults((prev) => ({ ...prev, loading: false }));
          }
        }
      };
      if (catalogVersionId && loaded) {
        performCicSearch();
      }
    } else {
      // Use traditional search
      fetch(`/api/products/search${query}&numResults=${numResults}`, { signal })
        .then((res) => res.json())
        .then((json) => {
          if (!signal.aborted) {
            setResults({ ...json, loading: false });
          }
        })
        .catch((error) => {
          if (!signal.aborted) {
            logger.withError(error).error('Product search error:');
            setResults((prev) => ({ ...prev, loading: false }));
          }
        });
    }

    return () => {
      abortController.abort();
    };
  }, [query, numResults, useCiC, catalogVersionId, search, loaded]);

  return results;
}
